import Api from '../../api'
import constants from '../../models/line-item-constants'
import { mapKeys, camelCase } from 'lodash'

let api = new Api()

export default class ItemStore {
  state = {}

  constructor(api) {
    this.api = api
  }

  async getLineItemLists(programId, invoiceId) {
    return this.getDropDownListForItems(programId, invoiceId, true)
  }

  async getDropDownListForItems(programId, invoiceId, loadAllData) {
    if (programId <= 0) return ''
    let response = ''
    response = await this.api.get(api.DropDownsListForItem(), {
      params: {
        programId: programId,
        invoiceId: invoiceId,
        loadAllData: loadAllData
      }
    })
    response.data = JSON.parse(JSON.stringify(response))
    return response.data
  }

  async getCostCentersBusinessUnits(programId, invoiceId) {
    if (programId <= 0) return ''
    let response = ''
    response = await this.api.get(api.GetCostCentersBusinessUnits(), {
      params: {
        programId: programId,
        invoiceId: invoiceId
      }
    })
    response.data = JSON.parse(JSON.stringify(response))
    return response.data
  }

  async getLineItemTaxes(supplierVci, categoryId, currencyCode, events) {
    let response = ''
    response = await this.api.get(api.GetSupplierTaxes(), {
      params: {
        supplierEoNumber: supplierVci,
        categoryId: categoryId,
        currencyCode: currencyCode,
        events: events
      }
    })
    response.data = JSON.parse(JSON.stringify(response))
    return response.data
  }

  async getLineItemFees(supplierVci, categoryId, currencyCode, events, buyerVendorId) {
    let response = ''
    response = await this.api.post(api.GetSupplierFees(), {
      supplierEoNumber: supplierVci,
      categoryId: categoryId,
      currencyCode: currencyCode,
      events: events,
      buyerVendorId: buyerVendorId
    })
    response.data = JSON.parse(JSON.stringify(response))
    return response.data
  }

  async getFeesAndDiscountItemData(programId, invoiceId) {
    return this.getDropDownListForItems(programId, invoiceId, false)
  }

  async computeItemTotal(lineItem, currency) {
    return this.api
      .post(api.ComputeLineItemTotals(), {
        lineItem: lineItem,
        currency: currency
      })
  }

  async computeSavingsItemTotal(lineItem, currency) {
    return this.api
      .post(api.SavingsComputeLineItemTotals(), {
        lineItem: lineItem,
        currency: currency
      })
  }

  async computeFeeTotal(fee, currency) {
    return this.api
      .post(api.ComputeFeeTotals(), {
        fee: fee,
        currency: currency
      })
  }

  async GetInvoiceTotalAmount(amount, vatAmount, fromCurrencyCode, toCurrencyCode) {
    return this.api
      .post(api.GetInvoiceTotalAmount(), {
        amount,
        vatAmount,
        fromCurrencyCode,
        toCurrencyCode
      })
  }

  async GetAmount(amount, invoiceId, fromCurrencyCode, toCurrencyCode) {
    return this.api
      .post(api.GetAmount(), {
        amount,
        invoiceId,
        fromCurrencyCode,
        toCurrencyCode
      })
  }

  populateTotalsOnFee(fee, feeWithTotals) {
    for (let i = 0; i < fee.taxes.length; i++) {
      let computedTax = feeWithTotals.taxes.find(function (tax) {
        return tax.name === fee.taxes[i].name
      })
      if (computedTax) {
        fee.taxes[i].value = computedTax.value
        fee.taxes[i].totalTaxes = computedTax.totalTaxes
      }
    }

    fee.totalTaxes = feeWithTotals.totalTaxes

    return fee
  }

  populateTotalsOnItem(lineItem, itemWithTotals, qtyAndUnitsOnPO) {
    lineItem.total = itemWithTotals.total
    lineItem.discountAmount = itemWithTotals.discountAmount
    lineItem.discountPercent = itemWithTotals.discountPercent
    lineItem.subtotal = itemWithTotals.subtotal
    lineItem.totalTaxes = itemWithTotals.totalTaxes
    lineItem.totalFees = itemWithTotals.totalFees
    lineItem.totalVAT = itemWithTotals.totalVAT
    lineItem.totalGST = itemWithTotals.totalGST
    lineItem.totalTaxesForFees = itemWithTotals.totalTaxesForFees
    lineItem.totalWithTaxesAndFees = itemWithTotals.totalWithTaxesAndFees

    lineItem.unitPriceSGR = itemWithTotals.unitPriceSGR
    lineItem.unitPricePreferredRate = itemWithTotals.unitPricePreferredRate
    lineItem.unitPriceNegotiatedRate = itemWithTotals.unitPriceNegotiatedRate
    lineItem.totalSGR = itemWithTotals.totalSGR
    lineItem.totalPreferredRate = itemWithTotals.totalPreferredRate
    lineItem.totalNegotiatedRate = itemWithTotals.totalNegotiatedRate

    if (lineItem.isFeeDiscountItem) {
      lineItem.taxTypeId = itemWithTotals.taxTypeId
      lineItem.feeTypeId = itemWithTotals.feeTypeId
      lineItem.customTypeId = itemWithTotals.customTypeId

      lineItem.costCenterAndBusinessUnit.businessUnitId = itemWithTotals.businessUnitId
      lineItem.costCenterAndBusinessUnit.costCenterId = itemWithTotals.costCenterId
      lineItem.costCenterAndBusinessUnit.programBusinessUnitAndCostCenterId = itemWithTotals.programBusinessUnitAndCostCenterId
    }

    for (let i = 0; i < lineItem.taxes.length; i++) {
      let computedTax = itemWithTotals.taxes.find(function (tax) {
        return tax.name === lineItem.taxes[i].name
      })
      if (computedTax) {
        lineItem.taxes[i].value = computedTax.value
        lineItem.taxes[i].totalTaxes = computedTax.totalTaxes
      }
    }

    for (let i = 0; i < lineItem.fees.length; i++) {
      let computedFee = itemWithTotals.fees.find(function (fee) {
        return fee.name === lineItem.fees[i].name
      })
      if (computedFee) {
        lineItem.fees[i].value = computedFee.value
        lineItem.fees[i].totalTaxes = computedFee.totalTaxes
        lineItem.fees[i].taxes = utils.copy(computedFee.taxes)
      }
    }

    // update PO qty remaining fields per item
    if (qtyAndUnitsOnPO && qtyAndUnitsOnPO.length > 0) {
      lineItem = this.updateItemAmountsOnDecoupling(lineItem, qtyAndUnitsOnPO, {
        ItemKey: lineItem.itemKey,
        Quantity: lineItem.quantity,
        Units: lineItem.units
      })
    }

    return lineItem
  }

  getEventsWithOrderedLineItems(events) {
    if (events && events.length > 0) {
      events.map(ev => {
        if (ev.lineItems && ev.lineItems.length > 0) {
          ev.lineItems = ev.lineItems.sort((a, b) => {
            if (Number(a.listOrder) < Number(b.listOrder)) return -1
            else if (Number(a.listOrder) > Number(b.listOrder)) return 1
            return 0
          })
        }
      })
    }
    return events
  }

  keepItemCustomFields(targetEvents, sourceEvents) {
    if (targetEvents && targetEvents.length > 0 &&
      sourceEvents && sourceEvents.length > 0) {
      targetEvents.map(ev => {
        if (ev.lineItems && ev.lineItems.length > 0) {
          let sourceEvent = sourceEvents.filter(e => e.uniqueId === ev.uniqueId)
          if (sourceEvent && sourceEvent.length > 0) {
            ev.lineItems.map(itm => {
              let sourceItem = sourceEvent[0].lineItems.filter(i => Number(i.listOrder) === Number(itm.listOrder))
              if (sourceItem && sourceItem.length > 0) {
                itm.customFields = sourceItem[0].customFields || []
              }
            })
          }
        }
      })
    }
    return targetEvents
  }

  updateItemsForI2PInvoiceOnEdit(poEvents, invoiceItems) {
    if (poEvents && poEvents.length > 0 &&
      invoiceItems && invoiceItems.length > 0) {
      poEvents.map(ev => {
        if (ev.lineItems && ev.lineItems.length > 0) {
          ev.lineItems.map(poItem => {
            // TODO: improve searching by ItemKey (must be updated on invoice after PO api call)
            let invItem = invoiceItems && invoiceItems.filter(v => +v.ListOrder === +poItem.listOrder)
            if (invItem && invItem.length > 0) {
              invItem = invItem[0]

              // TODO: move to backend
              if (!invItem.CustomFields) invItem.CustomFields = []
              for (let i = 0; i < invItem.CustomFields.length; i++) {
                invItem.CustomFields[i] = mapKeys(invItem.CustomFields[i], (v, k) => camelCase(k))
              }

              poItem.invoiceItemKey = invItem.ItemKey
              poItem.customFields = invItem.CustomFields
            }
          })
        }
      })
    }
    return poEvents
  }

  // create invoice updates
  updateEventsOnCreate(poEvents, invoicedItems, qtyAndUnitsOnPO) {
    if (poEvents && poEvents.length > 0) {
      poEvents.map(ev => {
        if (ev.lineItems && ev.lineItems.length > 0) {
          ev.lineItems.map(poItem => {
            // update item
            poItem = this.updateItemAmountsOnDecoupling(poItem, qtyAndUnitsOnPO)

            let itemSettings = qtyAndUnitsOnPO.filter(v => v.itemId === poItem.itemKey)[0]
            let canExceedPOQty = itemSettings ? itemSettings.canExceedPOQty : false

            if (!poItem.isAutoGenerated) {
              poItem.quantity = canExceedPOQty && poItem.isFeeDiscountItem && !poItem.isQuantityReadonly
                ? 1 : poItem.remainingQuantity
              poItem.units = poItem.remainingUnits
              poItem.unitPrice = poItem.isQuantityReadonly ? 0 : poItem.poUnitPrice
            }

            // taxes update
            let invItem = invoicedItems && invoicedItems.filter(v => v.Id === poItem.itemKey)
            if (invItem && invItem.length > 0) {
              invItem = invItem[0]

              poItem = this.updateItemFeesAndTaxesWithRemainingAmount(poItem, invItem)
            }

            if (poItem.isQuantityReadonly) {
              poItem = this.resetItemAmountsToZero(poItem)
            }
          })
        }
      })
    }
    return poEvents
  }

  // edit invoice updates
  getEventsWithChangesFromInvoice(poEvents, invoiceItems, qtyAndUnitsOnPO) {
    if (poEvents && poEvents.length > 0) {
      poEvents.map(ev => {
        if (ev.lineItems && ev.lineItems.length > 0) {
          // remove processing fees from PO; the ones saved at invoice level will be displayed
          // https://evedllc.atlassian.net/browse/PRODUCTDEV-1596
          let hasPFOnPO = ev.lineItems.some(i => i.isAutoGenerated && i.feeTypeId > 0)
          ev.lineItems = ev.lineItems.filter(i => !(i.isAutoGenerated && i.feeTypeId > 0))

          ev.lineItems.map(poItem => {
            // update item
            let invItem = invoiceItems && invoiceItems.filter(v => v.Id === poItem.itemKey)
            if (invItem && invItem.length > 0) {
              invItem = invItem[0]

              // populate new columns for P2P invoice
              poItem = this.updateItemAmountsOnDecoupling(poItem, qtyAndUnitsOnPO, invItem)
              if (invItem.Name !== '') {
                poItem.listOrder = invItem.ListOrder || poItem.listOrder
              }

              // update amounts with changes from invoice
              poItem.quantity = invItem.Quantity
              poItem.units = invItem.Units
              poItem.unitPrice = invItem.UnitPrice
              poItem.amountTypeValue = invItem.AmountTypeValue
              poItem.discountAmount = invItem.DiscountAmount
              poItem.discountPercent = invItem.DiscountPercent
              poItem.subtotal = invItem.Subtotal
              poItem.total = invItem.TotalAmount
              poItem.taxCode = invItem.TaxCode

              // TODO: move in backend
              if (!invItem.CustomFields) invItem.CustomFields = []
              for (let i = 0; i < invItem.CustomFields.length; i++) {
                invItem.CustomFields[i] = mapKeys(invItem.CustomFields[i], (v, k) => camelCase(k))
              }
              poItem.customFields = invItem.CustomFields

              // taxes update
              poItem = this.updateItemFeesAndTaxes(poItem, invItem)
            } else {
              poItem = this.updateItemAmountsOnDecoupling(poItem, qtyAndUnitsOnPO)

              poItem.quantity = 0
              poItem.units = 0
              poItem.unitPrice = poItem.poUnitPrice
              poItem.amountTypeValue = 0
              poItem.discountAmount = 0
              poItem.discountPercent = 0
              poItem.subtotal = 0
              poItem.total = 0

              if (poItem.remainingQuantity === 0 && poItem.itemTypeId !== null) {
                poItem = this.resetItemAmountsToZero(poItem)
              }
            }
          })

          // add items saved on invoice
          invoiceItems && invoiceItems.map(ii => {
            if (ii.Id === constants.defaultUniqueId) {
              ev.lineItems.push(this.mapInvoiceItemToPOItem(ii, ev, hasPFOnPO))
            }
          })
        }
      })

      // make sure listOrder was set correctly on items
      if (poEvents && poEvents.length !== 0) {
        poEvents = this.getEventsWithOrderedLineItems(poEvents)
        let listOrder = 1
        poEvents[0].lineItems.map(item => {
          if (+item.listOrder !== listOrder) {
            item.listOrder = listOrder
          }
          listOrder++
        })
      }
    }
    return poEvents
  }

  getDifferenceOrZero(amountOnPO, amountOnInv) {
    return amountOnPO > amountOnInv
      ? amountOnPO - amountOnInv
      : 0
  }

  updateItemFeesAndTaxesWithRemainingAmount(poItem, invItem) {
    // update taxes
    if (poItem.taxes && poItem.taxes.length > 0) {
      poItem.taxes.map(poTax => {
        let invTax = invItem.Taxes.filter(v => v.TaxTypeId === poTax.taxTypeId)
        if (invTax && invTax.length > 0) {
          invTax = invTax[0]
          poTax.percentage = invTax.Percent
          poTax.value = this.getDifferenceOrZero(poTax.value, invTax.Amount)
          poTax.isChecked = (invTax.Amount || 0) !== 0
        }
      })
    }

    // update fees
    if (poItem.fees && poItem.fees.length > 0) {
      poItem.fees.map(poFee => {
        let invFee = invItem.Fees.filter(v => v.FeeTypeId === poFee.feeTypeId)
        if (invFee && invFee.length > 0) {
          invFee = invFee[0]

          // will be skipped when fee is by percent and only the taxes per fee will be updated
          if ((Number(invFee.Percent) || 0) !== 0 || (Number(invFee.Amount) || 0) !== 0) {
            poFee.percentage = invFee.Percent
            poFee.value = this.getDifferenceOrZero(poFee.value, invFee.Amount)
          }

          poFee.totalTaxes = 0

          if (poFee.taxes && poFee.taxes.length > 0) {
            poFee.taxes.map(fTax => {
              if (invFee.Taxes) {
                let invTax = invFee.Taxes.filter(v => v.TaxTypeId === fTax.taxTypeId)
                if (invTax && invTax.length > 0) {
                  invTax = invTax[0]
                  fTax.percentage = invTax.Percent
                  fTax.value = this.getDifferenceOrZero(fTax.value, invTax.Amount)
                  fTax.isChecked = (invTax.Amount || 0) !== 0
                }
              }
              poFee.totalTaxes += Number(fTax.value)
            })
          }
        }
      })
    }
    return poItem
  }

  updateItemFeesAndTaxes(poItem, invItem) {
    // update taxes
    if (poItem.taxes && poItem.taxes.length > 0) {
      poItem.taxes.map(poTax => {
        let invTax = invItem.Taxes.filter(v => v.TaxTypeId === poTax.taxTypeId)
        if (invTax && invTax.length > 0) {
          invTax = invTax[0]
          poTax.percentage = invTax.Percent
          poTax.value = invTax.Amount
          poTax.isChecked = (invTax.Amount || 0) !== 0
        }
      })
    }

    // update fees
    if (poItem.fees && poItem.fees.length > 0) {
      poItem.fees.map(poFee => {
        let invFee = invItem.Fees.filter(v => v.FeeTypeId === poFee.feeTypeId)
        if (invFee && invFee.length > 0) {
          invFee = invFee[0]
          poFee.percentage = invFee.Percent
          poFee.value = invFee.Amount
          poFee.totalTaxes = invFee.TotalTaxesAmount || poFee.totalTaxes

          if (poFee.taxes && poFee.taxes.length > 0) {
            poFee.taxes.map(fTax => {
              if (invFee.Taxes) {
                let invTax = invFee.Taxes.filter(v => v.TaxTypeId === fTax.taxTypeId)
                if (invTax && invTax.length > 0) {
                  invTax = invTax[0]
                  fTax.percentage = invTax.Percent
                  fTax.value = invTax.Amount
                  fTax.isChecked = (invTax.Amount || 0) !== 0
                }
              }
            })
          }
        }
      })
    }

    return poItem
  }

  mapInvoiceItemToPOItem(invItem, _event, hasPFOnPO) {
    let usePairs = _event.lineItems[0].useBusinessUnitAndCostCenterMapping
    return {
      amountTypeId: invItem.AmountTypeId,
      amountTypeValue: invItem.AmountTypeValue || invItem.UnitPrice,
      costCenterAndBusinessUnit: {
        costCenterId: invItem.CostCenterId,
        costCenterNo: invItem.CostCenterNo,
        costCenterName: invItem.CostCenterName,
        businessUnitId: invItem.BusinessUnitId,
        businessUnitName: invItem.BusinessUnitName,
        costCenterAndBusinessUnitKey: usePairs ? invItem.ProgramBUAndCCId + '_' + invItem.BusinessUnitName + '_' + invItem.CostCenterNo : '',
        costCenterAndBusinessUnitName: usePairs ? invItem.BusinessUnitName + '_' + invItem.CostCenterNo + '(' + invItem.CostCenterName + ')' : '',
        programBusinessUnitAndCostCenterId: invItem.ProgramBUAndCCId
      },
      hasBusinessUnit: invItem.CostCenterId > 0,
      hasCostCenter: invItem.businessUnitId > 0,
      customTypeId: invItem.CustomTypeId,
      description: invItem.Description,
      internalNote: invItem.InternalNote,
      feeTypeId: invItem.FeeTypeId,
      fees: [],
      isAutoGenerated: invItem.IsAutoGenerated,
      entireRowInvalid: invItem.isAutoGenerated && invItem.feeTypeId > 0 && !hasPFOnPO,
      isFeeDiscountItem: invItem.ItemTypeId !== null,
      itemKey: constants.defaultUniqueId,
      invoiceItemKey: invItem.ItemKey || constants.defaultUniqueId,
      itemTypeId: invItem.ItemTypeId,
      listOrder: invItem.ListOrder,
      name: invItem.Name,
      quantity: invItem.Quantity,
      subtotal: invItem.Subtotal,
      taxTypeId: invItem.TaxTypeId,
      taxes: [],
      total: invItem.TotalAmount,
      totalAmount: invItem.TotalAmount,
      totalWithTaxesAndFees: invItem.TotalAmount,
      unitPrice: invItem.UnitPrice,
      useBusinessUnitAndCostCenterMapping: usePairs,
      maxUnitPrice: constants.maxAmount, // no limit for fees/taxes added on invoice
      taxCode: invItem.TaxCode
    }
  }

  resetItemAmountsToZero(item) {
    item.amountTypeValue = 0
    item.unitPrice = 0
    item.total = 0
    return item
  }

  updateItemAmountsOnDecoupling(lineItem, decouplingSettings, invoiceItem) { // !!!
    let settings = decouplingSettings && decouplingSettings.filter(v => v.itemId === lineItem.itemKey)
    if (settings && settings.length > 0) {
      settings = settings[0]

      lineItem.invoiceItemKey = settings.itemKey
      lineItem.poQuantity = settings.poQuantity
      lineItem.poUnits = settings.poUnits
      lineItem.poUnitPrice = settings.poUnitPrice

      let editedQty = (invoiceItem && invoiceItem.Quantity) || +settings.quantity
      let editedUnits = (invoiceItem && invoiceItem.Units) || +settings.units

      if (editedQty === 0 && invoiceItem && !lineItem.isAutoGenerated) {
        lineItem.quantity = 0
        lineItem.units = 0
        lineItem.amountTypeValue = 0
      }

      if (settings.canExceedPOQty) {
        lineItem = this.updateItemWithCheckByAmount(lineItem, settings, editedQty)
      } else {
        lineItem = this.updateItemWithCheckByQty(lineItem, settings, editedQty, editedUnits)
      }
    } else {
      // items added on invoice
      lineItem.invoiceItemKey = invoiceItem && invoiceItem.ItemKey
      lineItem.maxUnitPrice = constants.maxAmount
      lineItem.maxTotalAmount = constants.maxAmount
    }
    return lineItem
  }

  updateItemWithCheckByQty(lineItem, settings, editedQty, editedUnits) {
    // reset to 0 taxes and fees added by amount
    if (settings.initialQuantity === 0 && settings.remainingQuantity < settings.poQuantity) {
      if (lineItem.taxes && lineItem.taxes.length > 0) {
        lineItem.taxes.map(t => {
          if (!t.percentage) {
            t.value = 0
          }
        })
      }

      if (lineItem.fees && lineItem.fees.length > 0) {
        lineItem.fees.map(f => {
          if (!f.percentage) {
            f.value = 0
            if (f.taxes && f.taxes.length > 0) {
              f.taxes.map(ft => {
                if (!ft.percentage) {
                  ft.value = 0
                }
              })
            }
          }
        })
      }
    }

    // = PO Qty - Invoiced Qty - (Edited Qty - Last Saved Qty)
    lineItem.remainingQuantity = settings.poQuantity - settings.invoicedQuantity - (editedQty - (settings.initialQuantity || 0))
    lineItem.remainingUnits = settings.poUnits - settings.invoicedUnits - (editedUnits - (settings.initialUnits || 0))

    lineItem.maxQuantity = settings.poQuantity - settings.invoicedQuantity + settings.initialQuantity
    lineItem.maxUnitPrice = lineItem.isFeeDiscountItem ? constants.maxAmount : settings.poUnitPrice
    lineItem.maxTotalAmount = constants.maxAmount

    if (lineItem.remainingQuantity < 0) lineItem.remainingQuantity = 0
    if (lineItem.remainingUnits < 0) lineItem.remainingUnits = 0

    lineItem.isQuantityReadonly = lineItem.remainingQuantity === 0 && editedQty === 0
    lineItem.isUnitsReadonly = lineItem.isQuantityReadonly

    return lineItem
  }

  updateItemWithCheckByAmount(lineItem, settings, editedQty) {
    lineItem.maxUnitPrice = lineItem.isFeeDiscountItem // && settings.excludeTaxes
      ? constants.maxAmount : settings.poUnitPrice
    lineItem.maxTotalAmount = this.getMaxTotalAmount(lineItem, settings)
    // set min validation threshold only for Discount items
    lineItem.minTotalAmount = !lineItem.isAutoGenerated && lineItem.totalWithTaxesAndFees < 0
      ? settings.poTotalAmount : (Number(lineItem.total) < 0 ? Number(lineItem.total) : 0)

    // when excluding taxes the total without taxes will be validated instead of Total Price column
    lineItem.totalToValidate = !lineItem.isFeeDiscountItem && settings.excludeTaxes
      ? settings.totalToValidate || lineItem.total
      : null

    lineItem.hideQtyWhenZero = !lineItem.isFeeDiscountItem
    if (lineItem.hideQtyWhenZero && Number(lineItem.quantity) === 0) {
      lineItem.totalTaxes = 0
      lineItem.totalFees = 0
      lineItem.totalTaxesForFees = 0
      lineItem.total = 0
      lineItem.totalWithTaxesAndFees = 0
    }

    // display available amount on taxes/fees/discount
    if (lineItem.isFeeDiscountItem && !lineItem.isAutoGenerated) {
      if (Number(lineItem.total) !== 0 && Number(lineItem.amountTypeId) === 1) {
        let prevAmount = lineItem.amountTypeValue
        lineItem.amountTypeValue = this.getRemainingTotalAmountPerItem(false, settings)

        if (Number(lineItem.itemTypeId) === 3) { // Discount: amount must be positive
          lineItem.amountTypeValue = Math.abs(lineItem.amountTypeValue)
        } else {
          // keep positive po item amount when excluding taxes is ON and remaining amount is negative
          if (Number(prevAmount) > 0 && Number(lineItem.amountTypeValue) < 0) {
            lineItem.amountTypeValue = prevAmount
          }
        }

        lineItem.unitPrice = lineItem.amountTypeValue
      }
    }

    lineItem.remainingQuantity = 0
    lineItem.remainingUnits = 0
    lineItem.maxQuantity = constants.maxAmount

    lineItem.isQuantityReadonly = !lineItem.isAutoGenerated && editedQty === 0 &&
      ((settings.poTotalAmount >= 0 && settings.poTotalAmount <= settings.invoicedTotalAmount) ||
      (settings.poTotalAmount < 0 && settings.poTotalAmount >= settings.invoicedTotalAmount))
    lineItem.isUnitsReadonly = lineItem.isQuantityReadonly

    return lineItem
  }

  getMaxTotalAmount(lineItem, qtySettings) {
    let maxTotalAmount = lineItem.totalWithTaxesAndFees < 0 ? 0 : constants.maxAmount

    if (!lineItem.isAutoGenerated && lineItem.totalWithTaxesAndFees >= 0) {
      if (lineItem.isFeeDiscountItem /* && qtySettings.excludeTaxes */) {
        // taxes are ignored, can enter any amount
        maxTotalAmount = constants.maxAmount
      } else {
        maxTotalAmount = this.getRemainingTotalAmountPerItem(qtySettings.excludeTaxes, qtySettings)
      }
    }

    return maxTotalAmount
  }

  getRemainingTotalAmountPerItem(excludeTaxes, settings) {
    let result = excludeTaxes
      ? settings.poTotalAmountWithoutTaxes - settings.invoicedTotalWithoutTaxes +
      settings.initialTotalWithoutTaxes
      : settings.poTotalAmount - settings.invoicedTotalAmount + settings.initialTotalAmount
    return parseFloat(result.toFixed(4))
  }

  getDecouplingSettingsPerItem(poEvents, invoiceItems, settings) {
    let result = []

    if (poEvents && poEvents.length > 0) {
      poEvents.map(ev => {
        if (ev.lineItems && ev.lineItems.length > 0) {
          ev.lineItems.map(poItem => {
            let invItem = invoiceItems && invoiceItems.filter(v => v.Id === poItem.itemKey)
            if (invItem && invItem.length > 0) invItem = invItem[0]

            let poUnitPrice = poItem.isFeeDiscountItem ? poItem.amountTypeValue : poItem.unitPrice

            result.push({
              itemId: poItem.itemKey,
              itemKey: invItem.ItemKey,

              quantity: (invItem && invItem.Quantity) || 0,
              units: (invItem && invItem.Units) || 0,
              unitPrice: (invItem && invItem.UnitPrice) || 0,
              totalToValidate: invItem && settings.ExcludeTaxes ? invItem.TotalAmountWithoutTaxes : null,

              poQuantity: Number(poItem.quantity || 0),
              poUnits: Number(poItem.units || 0),
              poUnitPrice: Number(poUnitPrice || 0),
              poTotalAmount: poItem.totalWithTaxesAndFees,
              poTotalAmountWithoutTaxes: poItem.total,

              invoicedQuantity: (invItem && invItem.InvoicedQuantity) || 0,
              invoicedUnits: (invItem && invItem.InvoicedUnits) || 0,
              invoicedTotalAmount: (invItem && invItem.InvoicedTotalAmount) || 0,
              invoicedTotalWithoutTaxes: (invItem && invItem.InvoicedTotalWithoutTaxes) || 0,

              initialQuantity: (invItem && invItem.Quantity) || 0,
              initialUnits: (invItem && invItem.Units) || 0,
              initialTotalAmount: (invItem && invItem.TotalAmount) || 0,
              initialTotalWithoutTaxes: (invItem && invItem.TotalAmountWithoutTaxes) || 0,

              canExceedPOQty: settings.AllowExceedPOQty,
              excludeTaxes: settings.ExcludeTaxes
            })
          })
        }
      })
    }
    return result
  }
}



// WEBPACK FOOTER //
// ./src/components/_modules/I2P/dialogs/LineItem/LineItemStore.js